import React, { useState, useEffect, FC, useMemo } from 'react';
import { getItemDetails } from '../services/web3Service';
import { updateCertificateSponsored, getOrCreateSmartAccount } from '../services/smartAccountService';
import { uploadJsonToIPFS, uploadFileToIPFS } from '../services/ipfsUploader';
import { IPFS_GATEWAY_PREFIX } from '../constants';
import AlertModal from '../components/AlertModal';
import './UpdateCertificatePage.css';
import { useTranslation } from 'react-i18next';
import Select, { MultiValue } from 'react-select';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { TagOption, fetchRawMaterialTags, createRawMaterialTag } from '../services/api';
import { customSelectStyles } from '../SelectStyles';

interface CategoryOption {
    value: string;
    label: string;
}

// Re-using categories from MintCertificateFormx
const categories = [
    "Textile arts (e.g. knitting, embroidery, quilting)",
    "Woodcrafts (e.g. carved objects, furniture)",
    "Ceramics & pottery",
    "Jewelry making",
    "Leatherwork",
    "Paper crafts (e.g. origami, scrapbooking)",
    "Glasswork (e.g. stained glass, blown glass)",
    "Traditional folk arts (e.g. Turkish ebru, Japanese urushi, Scandinavian rosemaling)",
    "Textile design",
    "Basket weaving",
    "Landscape Architecture and Biophilia Design",
    "Accessories",
    "Natural dyeing",
    "Indigo dyeing",
    "Weaving",
    "Tapestry",
    "Spinning",
    "Felting",
    "Fibre processing (linen, tow, wool, silk, etc.)",
    "Cultivation of dye plants",
    "Cultivation of plants for textile fibres",
    "Machine knitting",
    "Bookbinding",
    "Furniture"
];

const MAX_IMAGE_SIZE_MB = 1;
const MAX_TOTAL_SIZE_MB = 20;
const MAX_TEXT_LENGTH = 256;
const ACCEPTED_DOC_TYPES = '.pdf,.png,.jpg,.jpeg,.webp';

interface UpdateCertificatePageProps {
    tokenId: string;
    onCertificateUpdated: (tokenId: string) => void;
    onBack: () => void;
}

const UpdateCertificatePage: FC<UpdateCertificatePageProps> = ({ tokenId, onCertificateUpdated, onBack }) => {
    // Form state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedCategories, setSelectedCategories] = useState<readonly CategoryOption[]>([]);
    const [rawMaterialTags, setRawMaterialTags] = useState<readonly TagOption[]>([]);

    const [vintageYear, setVintageYear] = useState('');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
    const [documentFiles, setDocumentFiles] = useState<File[]>([]);
    const [existingDocuments, setExistingDocuments] = useState<{ name: string; value: string }[]>([]);
    const { t } = useTranslation();

    const categoryOptions: CategoryOption[] = useMemo(() =>
        categories.map((cat, index) => ({
            value: cat,
            label: t(`categories.${index}`)
        })),
        [t]
    );

    const handleCategoryChange = (newValue: MultiValue<CategoryOption>) => {
        setSelectedCategories(newValue);
    };

    // Data state
    const [currentCertificateURI, setCurrentCertificateURI] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true); // For initial data load
    const [isCreating, setIsCreating] = useState(false); // For new tag creation

    const [isUpdating, setIsUpdating] = useState(false);

    // Alert modal state
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState<'success' | 'error'>('success');
    const [alertTxHash, setAlertTxHash] = useState<string | undefined>();
    const [pendingRedirect, setPendingRedirect] = useState(false);

    useEffect(() => {
        const fetchCertificateData = async () => {
            setIsLoading(true);
            try {
                const details = await getItemDetails(tokenId);
                const cert = details.certificate;
                setName(cert.name);
                setDescription(cert.description);
                setImagePreviewUrl(cert.image); // This is the full IPFS gateway URL
                setCurrentCertificateURI(details.currentCertificateURI);

                // Extract attributes
                const attributes = cert.attributes || [];

                const categoryAttrs = attributes.filter(a => a.trait_type === "Category");
                setSelectedCategories(categoryAttrs.map(a => {
                    const idx = categories.indexOf(String(a.value));
                    return { value: String(a.value), label: idx >= 0 ? t(`categories.${idx}`) : String(a.value) };
                }));

                const yearAttr = attributes.find(a => a.trait_type === "Vintage Year");

                const existingTags = attributes
                    .filter(attr => attr.trait_type === "Raw Materials" || attr.trait_type === "Raw Material")
                    .map(attr => ({ value: String(attr.value).toLowerCase().replace(/\W/g, ''), label: String(attr.value) }));

                setRawMaterialTags(existingTags);

                const docAttrs = attributes
                    .filter(a => a.trait_type === "Document")
                    .map(a => ({ name: (a as any).name || 'Document', value: String(a.value) }));
                setExistingDocuments(docAttrs);

                setVintageYear(yearAttr?.value?.toString() || '');

            } catch (error) {
                console.error("Failed to fetch certificate details:", error);
                showAlert(t('errorTitle'), t('alert.loadCertificateError'), 'error');
            } finally {
                setIsLoading(false);
            }
        };

        fetchCertificateData();
    }, [tokenId, t]);

    const showAlert = (title: string, message: string, type: 'success' | 'error', txHash?: string) => {
        setAlertTitle(title);
        setAlertMessage(message);
        setAlertType(type);
        setAlertTxHash(txHash);
        setAlertOpen(true);
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const MAX_FILE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

            if (file.size > MAX_FILE_SIZE_BYTES) {
                showAlert(t('errorTitle'), t('alert.fileTooLarge', { maxSize: `${MAX_IMAGE_SIZE_MB}MB` }), 'error');
                e.target.value = '';
                return;
            }

            setImageFile(file);
            setImagePreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleDocumentsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const newFiles = Array.from(e.target.files);
        const allFiles = [...documentFiles, ...newFiles];
        const totalSize = allFiles.reduce((sum, f) => sum + f.size, 0) + (imageFile?.size || 0);
        if (totalSize > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
            showAlert(t('errorTitle'), t('alert.totalSizeTooLarge', { maxSize: `${MAX_TOTAL_SIZE_MB}MB` }), 'error');
            e.target.value = '';
            return;
        }
        setDocumentFiles(allFiles);
        e.target.value = '';
    };

    const removeDocument = (index: number) => {
        setDocumentFiles(prev => prev.filter((_, i) => i !== index));
    };

    const removeExistingDocument = (index: number) => {
        setExistingDocuments(prev => prev.filter((_, i) => i !== index));
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        if (value.length > MAX_TEXT_LENGTH) {
            showAlert(t('errorTitle'), t('alert.textTooLong', { maxLength: MAX_TEXT_LENGTH }), 'error');
        }
        setName(value.substring(0, MAX_TEXT_LENGTH));
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { value } = e.target;
        if (value.length > MAX_TEXT_LENGTH) {
            showAlert(t('errorTitle'), t('alert.textTooLong', { maxLength: MAX_TEXT_LENGTH }), 'error');
        }
        setDescription(value.substring(0, MAX_TEXT_LENGTH));
    };

    const handleRawMaterialChange = (newValue: MultiValue<TagOption>) => {
        setRawMaterialTags(newValue);
    };

    const handleCreateTag = async (inputValue: string) => {
        setIsCreating(true);
        try {
            const newOption = await createRawMaterialTag(inputValue);
            setRawMaterialTags([...rawMaterialTags, newOption]);
        } catch (err) {
            console.error("Failed to create tag:", err);
            showAlert(t('errorTitle'), t('alert.tagCreationError', { error: err instanceof Error ? err.message : 'Unknown error' }), 'error');
        } finally {
            setIsCreating(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name || !description || selectedCategories.length === 0) {
            showAlert(t('errorTitle'), t('alert.validationErrorMessage'), 'error');
            return;
        }
        if (!tokenId || !currentCertificateURI) {
            showAlert(t('errorTitle'), t('alert.missingTokenIdOrUri'), 'error');
            return;
        }
        setIsUpdating(true);

        try {
            let imageUri = imagePreviewUrl || null;

            // 1. If a new image is selected, upload it to IPFS
            if (imageFile) {
                const imageHash = await uploadFileToIPFS(imageFile);
                imageUri = `${IPFS_GATEWAY_PREFIX}${imageHash}`;
            }

            if (!imageUri) {
                throw new Error("Image for certificate is missing.");
            }

            const newDocHashes: string[] = [];
            for (const doc of documentFiles) {
                const hash = await uploadFileToIPFS(doc);
                newDocHashes.push(hash);
            }

            const newMetadata = {
                name,
                description,
                image: imageUri,
                previous_certificate_uri: currentCertificateURI.replace(IPFS_GATEWAY_PREFIX, 'ipfs://'),
                attributes: [
                    ...selectedCategories.map(c => ({ trait_type: "Category", value: c.value })),
                    ...rawMaterialTags.map(tag => ({ trait_type: "Raw Material", value: tag.label })),
                    ...(vintageYear ? [{ trait_type: "Vintage Year", value: vintageYear }] : []),
                    ...existingDocuments.map(doc => ({ trait_type: "Document", value: doc.value, name: doc.name })),
                    ...newDocHashes.map((hash, i) => ({ trait_type: "Document", value: `${IPFS_GATEWAY_PREFIX}${hash}`, name: documentFiles[i].name })),
                ],
            };

            // 3. Upload the new metadata JSON to IPFS
            const newMetadataHash = await uploadJsonToIPFS(newMetadata);
            const newMetadataURI = `ipfs://${newMetadataHash}`;

            // Ensure user has a smart account
            await getOrCreateSmartAccount();

            // 4. Update certificate via backend (sponsored transaction)
            const result = await updateCertificateSponsored(tokenId, newMetadataURI);

            if (!result.success) {
                throw new Error(result.error || 'Certificate update failed');
            }

            showAlert(t('successTitle'), t('updateSuccessAlert'), 'success', result.transactionHash);
            setPendingRedirect(true);

        } catch (error) {
            console.error("Failed to update certificate:", error);
            const errorMessage = error instanceof Error ? error.message : "An unknown error occurred.";
            showAlert(t('errorTitle'), t('alert.updateFailed', { errorMessage }), 'error');
        } finally {
            setIsUpdating(false);
        }
    };

    if (isLoading) {
        return <div className="loading-container">{t('loadingCertificateData')}</div>;
    }

    return (
        <div className="update-certificate-container">
            <button onClick={onBack} className="back-button">{t('backToSearch')}</button>
            <h2>{t('pages.update.titleWithId', { tokenId })}</h2>
            <p className="previous-uri-link">
                {t('pages.update.previousLabel')} <a href={currentCertificateURI} target="_blank" rel="noopener noreferrer">{currentCertificateURI}</a>
            </p>
            <form onSubmit={handleSubmit} className="update-certificate-form">
                <div className="form-group">
                    <label htmlFor="name">{t('form.nameLabel')}</label>
                    <input id="name" type="text" value={name} onChange={handleNameChange} required maxLength={MAX_TEXT_LENGTH} />
                    <div className="char-counter">{name.length} / {MAX_TEXT_LENGTH}</div>
                </div>
                <div className="form-group">
                    <label htmlFor="description">{t('form.descriptionLabel')}</label>
                    <textarea id="description" value={description} onChange={handleDescriptionChange} required maxLength={MAX_TEXT_LENGTH} />
                    <div className="char-counter">{description.length} / {MAX_TEXT_LENGTH}</div>
                </div>
                <div className="form-group">
                    <label htmlFor="category">{t('form.categoryLabel')}</label>
                    <Select<CategoryOption, true>
                        inputId="category"
                        isMulti
                        isClearable
                        options={categoryOptions}
                        value={selectedCategories}
                        onChange={handleCategoryChange}
                        styles={customSelectStyles as any}
                        placeholder={t('form.categoryPlaceholder', 'Select categories...')}
                        isDisabled={isUpdating}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="rawMaterials">{t('form.rawMaterialsLabel')}</label>
                    <AsyncCreatableSelect
                        id="rawMaterials"
                        isMulti
                        isClearable
                        styles={customSelectStyles}
                        isDisabled={isCreating || isUpdating}
                        isLoading={isCreating}
                        onChange={handleRawMaterialChange}
                        onCreateOption={handleCreateTag}
                        formatCreateLabel={(inputValue) => `${t('createTagLabel', 'Create')} "${inputValue}"`}
                        getNewOptionData={(inputValue) => ({ value: inputValue.toLowerCase().replace(/\W/g, ''), label: inputValue, __isNew__: true } as TagOption)}
                        loadOptions={fetchRawMaterialTags}
                        placeholder={t('form.rawMaterialsPlaceholder', 'Select or create materials...')}
                        value={rawMaterialTags}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="vintageYear">{t('form.vintageYearLabel')}</label>
                    <input id="vintageYear" type="number" value={vintageYear} onChange={(e) => setVintageYear(e.target.value)} placeholder="e.g., 2023" />
                </div>
                <div className="form-group">
                    <label htmlFor="image">{t('form.updateImageLabel')}</label>
                    <input id="image" type="file" onChange={handleImageChange} accept="image/*" />{imagePreviewUrl && (
                        <div className="image-preview-container">
                            <p>{t('form.imagePreviewLabel')}</p>
                            <img src={imagePreviewUrl} alt="Certificate Preview" className="image-preview" />
                        </div>
                    )}
                </div>
                <div className="form-group">
                    <label htmlFor="documents">{t('form.documentsLabel')}</label>
                    <p className="field-description">{t('form.documentsDescription', { maxSize: MAX_TOTAL_SIZE_MB })}</p>
                    {existingDocuments.length > 0 && (
                        <ul className="document-list">
                            {existingDocuments.map((doc, index) => (
                                <li key={`existing-${index}`}>
                                    <a href={doc.value} target="_blank" rel="noopener noreferrer">{doc.name}</a>
                                    <button type="button" className="remove-doc-button" onClick={() => removeExistingDocument(index)}>&times;</button>
                                </li>
                            ))}
                        </ul>
                    )}
                    <input id="documents" type="file" accept={ACCEPTED_DOC_TYPES} multiple onChange={handleDocumentsChange} />
                    {documentFiles.length > 0 && (
                        <ul className="document-list">
                            {documentFiles.map((file, index) => (
                                <li key={index}>
                                    <span>{file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                                    <button type="button" className="remove-doc-button" onClick={() => removeDocument(index)}>&times;</button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <button type="submit" disabled={isUpdating}>
                    {isUpdating ? t('updatingButton') : t('updateCertificateButton')}
                </button>
            </form>
            <AlertModal
                isOpen={alertOpen}
                onClose={() => {
                    setAlertOpen(false);
                    if (pendingRedirect) {
                        onCertificateUpdated(tokenId);
                    }
                }}
                title={alertTitle}
                message={alertMessage}
                type={alertType}
                txHash={alertTxHash}
            />
        </div>
    );
};

export default UpdateCertificatePage;